import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CircleCheck as CheckCircle2, ArrowRight } from 'lucide-react';
export default function BathroomAnswer() {
  const includedItems = [{
    title: "Custom vanities & storage",
    description: "Tailored double or single vanities with soft-close cabinetry, quartz or marble tops, and configured drawer storage."
  }, {
    title: "Full-surface wall & floor tile",
    description: "Subway, zellige, or large-format porcelain installations paired with heated flooring systems and durable waterproof membranes."
  }, {
    title: "Custom walk-in showers & soaking tubs",
    description: "Frameless glass enclosures, built-in recessed niches, bench seating, rainfall heads, and standalone soaking tubs."
  }, {
    title: "High-efficiency ventilation & climate control",
    description: "Whisper-quiet exhaust fans with humidity sensing to protect finishes, prevent mold, and maintain balanced airflow."
  }, {
    title: "Premium fixtures & layered lighting",
    description: "Solid brass faucets, thermostatic valves, pressure-balanced hardware, vanity task sconces, and dimmable overhead lighting."
  }];
  return <section id="herobathroomremodelanswer" data-nav="light" className="relative overflow-hidden bg-background text-foreground pt-12 pb-20 lg:py-24 border-b border-border/60">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          {/* Left Column: Heading, Direct Answer, and What's Included */}
          <div className="lg:col-span-7 flex flex-col justify-center">
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-[1.15] mb-6">
              What does a comprehensive bathroom remodel in Brookhaven entail?
            </h1>

            {/* Direct 2-3 sentence answer */}
            <div className="text-base sm:text-lg text-foreground/90 leading-relaxed mb-8 space-y-3">
              <p>
                A complete Brookhaven bathroom renovation transforms an outdated or inefficient room into an architecturally sound, moisture-sealed space engineered for daily luxury. The process involves full structural framing adjustments, comprehensive subfloor and waterproof backing prep, and updated plumbing and electrical circuits to support modern demands.
              </p>
              <p>
                From meticulous tile installation to high-performance ventilation, every component is coordinated to meet local building codes while delivering enduring comfort and elevated home value.
              </p>
            </div>

            {/* What's Included Section */}
            <div className="space-y-4">
              <h2 className="text-xl font-serif font-semibold tracking-tight text-foreground">
                What’s Included in Every Renovation
              </h2>
              
              <Card className="bg-card border-border/80 shadow-sm py-4">
                <CardContent className="p-4 sm:p-6 divide-y divide-border/60 space-y-4 sm:space-y-0">
                  {includedItems.map((item, index) => <div key={item.title} data-index={index} className="flex items-start gap-3.5 pt-4 first:pt-0">
                      <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                      <div>
                        <span className="font-semibold text-foreground text-sm sm:text-base block">
                          {item.title}
                        </span>
                        <p className="text-sm text-foreground/80 mt-0.5 leading-normal">
                          {item.description}
                        </p>
                      </div>
                    </div>)}
                </CardContent>
              </Card>
            </div>

            {/* Action buttons */}
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="font-medium shadow-sm">
                <Link to="/ContactRequestAnEstimatePage">
                  Request an estimate
                  <ArrowRight className="size-4 ml-1.5" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="font-medium">
                <Link to="/OurProjectsPage">
                  View past bathroom projects
                </Link>
              </Button>
            </div>
          </div>

          {/* Right Column: Realistic Photographic Showcase */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            <div className="relative rounded-2xl overflow-hidden shadow-lg border border-border bg-muted">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/luxury_bathroom_remodel_brookhaven.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/luxury_bathroom_remodel_brookhaven.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/luxury_bathroom_remodel_brookhaven.png-wvc-sizes" alt="Renovated master bathroom with freestanding soaking tub, custom double vanity, and walk-in frameless glass shower in Brookhaven" loading="eager" decoding="async" className="w-full h-auto aspect-[4/5] object-cover object-center" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
              <div className="absolute bottom-4 left-4 right-4 p-4 rounded-xl bg-background/90 backdrop-blur-xs border border-border shadow-xs">
                <p className="text-xs font-medium text-foreground tracking-wide uppercase">
                  Brookhaven Residential Renovation
                </p>
                <p className="text-sm text-foreground/80 mt-0.5">
                  Complete master ensuite redesign featuring precision tile work, custom millwork, and balanced natural illumination.
                </p>
              </div>
            </div>

            {/* Secondary detail image */}
            <div className="relative rounded-xl overflow-hidden shadow-sm border border-border bg-muted hidden sm:block">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/bathroom_tile_vanity_detail.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/bathroom_tile_vanity_detail.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/bathroom_tile_vanity_detail.webp-wvc-sizes" alt="Detailed view of brushed brass fixtures, marble vanity counter, and herringbone shower tile installation" loading="lazy" decoding="async" className="w-full h-44 object-cover object-center" />
            </div>
          </div>

        </div>
      </div>
    </section>;
}